import { Badge, Button, Group, Modal, Select, SimpleGrid, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { DataTable } from "../../components/DataTable";
import { PageHeader } from "../../components/ui";
import { useOpstina } from "../../context/opstina";
import { useT } from "../../i18n/script";

export interface Producer {
  id: number; bpg: string; name: string; holder_name?: string; holding_type?: string; category?: string; jib?: string;
  address?: string; rpg_status?: string; opstina?: string; opstina_code?: string; admin_unit_id?: number; orchard_count: number;
  area_ha: number; tree_count: number; species: string[]; note?: string; contact?: Record<string, string>;
}

export function ProducerForm({ initial, onDone }: { initial?: Partial<Producer>; onDone: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const { units, opstina } = useOpstina();
  const f = useForm({
    initialValues: {
      bpg: initial?.bpg ?? "", name: initial?.name ?? "", holder_name: initial?.holder_name ?? "",
      holding_type: initial?.holding_type ?? "fizicko_lice", category: initial?.category ?? "komercijalno", jib: initial?.jib ?? "",
      admin_unit_id: String(initial?.admin_unit_id ?? units.find((u) => u.code === opstina)?.id ?? ""), address: initial?.address ?? "",
      rpg_status: initial?.rpg_status ?? "aktivan", note: initial?.note ?? "",
    },
    validate: { bpg: (v) => (/^[0-9]{6,15}$/.test(v) ? null : t("BPG: 6–15 cifara")), name: (v) => (v ? null : t("Obavezno")),
      admin_unit_id: (v) => (v ? null : t("Obavezno")) },
  });
  const save = useMutation({
    mutationFn: (v: typeof f.values) => {
      const body = { ...v, admin_unit_id: Number(v.admin_unit_id), jib: v.jib || null };
      return initial?.id ? http.put(`/api/v1/producers/${initial.id}`, body) : http.post("/api/v1/producers", body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["producers"] }); qc.invalidateQueries({ queryKey: ["producer"] }); onDone(); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  return (
    <form onSubmit={f.onSubmit((v) => save.mutate(v))}>
      <Stack>
        <SimpleGrid cols={2}>
          <TextInput label="BPG" required {...f.getInputProps("bpg")} disabled={!!initial?.id} />
          <Select label={t("Opština")} required searchable data={units.map((u) => ({ value: String(u.id), label: u.name }))} {...f.getInputProps("admin_unit_id")} />
          <TextInput label={t("Naziv gazdinstva")} required {...f.getInputProps("name")} />
          <TextInput label={t("Nosilac gazdinstva")} {...f.getInputProps("holder_name")} />
          <Select label={t("Oblik")} data={[{ value: "fizicko_lice", label: t("Fizičko lice") }, { value: "preduzetnik", label: t("Preduzetnik") },
            { value: "pravno_lice", label: t("Pravno lice") }]} {...f.getInputProps("holding_type")} />
          <Select label={t("Kategorija")} data={[{ value: "komercijalno", label: t("Komercijalno") }, { value: "nekomercijalno", label: t("Nekomercijalno") },
            { value: "pravno_lice", label: t("Pravno lice") }]} {...f.getInputProps("category")} />
          <TextInput label="JIB" {...f.getInputProps("jib")} />
          <Select label={t("Status u RPG")} data={["aktivan", "pasivan"]} {...f.getInputProps("rpg_status")} />
        </SimpleGrid>
        <TextInput label={t("Adresa")} {...f.getInputProps("address")} />
        <TextInput label={t("Napomena")} {...f.getInputProps("note")} />
        <Group justify="flex-end"><Button type="submit" loading={save.isPending}>{t("Sačuvaj")}</Button></Group>
      </Stack>
    </form>
  );
}

export default function ProducersPage() {
  const { t } = useT();
  const can = useCan();
  const nav = useNavigate();
  const [species, setSpecies] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const list = useQuery({ queryKey: ["producers", species], queryFn: () => http.get<Producer[]>("/api/v1/producers", { species, limit: 2000 }) });
  const spList = useQuery({ queryKey: ["vrsta_voca"], staleTime: 600_000,
    queryFn: () => http.get<{ code: string; name: string }[]>("/api/v1/codelists/vrsta_voca/items") });
  const spName = (c: string) => spList.data?.find((x) => x.code === c.toUpperCase())?.name ?? c;
  const allSpecies = Array.from(new Set((list.data ?? []).flatMap((p) => p.species))).sort()
    .map((c) => ({ value: c, label: spName(c) }));
  return (
    <>
      <PageHeader title="Proizvođači" description="Gazdinstva u voćarstvu u vašoj nadležnosti (opština iz zaglavlja filtrira listu)."
        right={<>
          <Select placeholder={t("Vrsta voća")} clearable data={allSpecies} value={species} onChange={setSpecies} w={170} />
          {can("producers:write") && <Button leftSection={<IconPlus size={16} />} onClick={() => setOpen(true)}>{t("Novi proizvođač")}</Button>}
        </>} />
      <DataTable rows={(list.data ?? []) as unknown as Record<string, unknown>[]} exportName="proizvodjaci"
        onRowClick={(r) => nav(`/gazdinstva/${r.id}`)} columns={[
          { key: "bpg", label: "BPG" }, { key: "name", label: "Naziv" }, { key: "holder_name", label: "Nosilac" },
          { key: "opstina", label: "Opština" }, { key: "category", label: "Kategorija" },
          { key: "orchard_count", label: "Voćnjaka", type: "integer" }, { key: "area_ha", label: "Površina (ha)", type: "number" },
          { key: "tree_count", label: "Stabala", type: "integer" },
          { key: "species", label: "Vrste", render: (r) => <Group gap={4}>{(r.species as string[]).map((s) => <Badge key={s} size="xs" variant="light">{spName(s)}</Badge>)}</Group> },
          { key: "rpg_status", label: "RPG", render: (r) => <Badge size="xs" color={r.rpg_status === "aktivan" ? "green" : "gray"}>{String(r.rpg_status)}</Badge> },
        ]} />
      <Modal opened={open} onClose={() => setOpen(false)} title={t("Novi proizvođač")} size="lg">
        <ProducerForm onDone={() => setOpen(false)} />
      </Modal>
    </>
  );
}
